import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useImmerReducer } from "use-immer";
import { toast } from "react-toastify";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import useProjectOrganisationContext from "@/pages/takeoff/context/project-organisation/use-project-organisation-context.ts";
import ENDPOINTS, { PROJECT_REFERENCE_ENDPOINTS } from "@/services/endpoints.ts";

import { putItem } from "@/queries/queries-helper.ts";
import projectFilePlanReducer from "./project-file-plan-reducer.ts";

import UpdateStoreyFileDataDTO from "@/models/interfaces/dtos/project-references/update/update-storey-file-data-dto.ts";
import { ProjectFilePlanContext, ProjectFilePlanContextProps } from "./project-file-plan-context-props.ts";
import ProjectFilePlanReducerAction from "./project-file-plan-reducer-action.ts";
import PlanFileData from "@/pages/takeoff/context/project-measure/models/plan-file-data.ts";
import KnownScaleContent from "@/pages/takeoff/context/project-organisation/models/known-scale-content.ts";
import ScaleModeType from "@/pages/takeoff/context/project-organisation/models/enums/file-content-type.ts";
import UserDefinedContent from "@/pages/takeoff/context/project-organisation/models/user-defined-content.ts";

interface ProjectFilePlanContextProviderProps extends React.PropsWithChildren {
    fileId: string;
}

const ProjectFilePlanContextProvider: React.FC<ProjectFilePlanContextProviderProps> = ({ fileId, children }) => {
    const { project } = useProjectContext();

    const { data: projectData, getPlanFileData, setPlanFileData, getBaamerFileRevisionIdFromStoreyId } = useProjectOrganisationContext();

    const { building, storey, file } = useMemo(() => {
        if (projectData == null) return undefined;
        return projectData.findBuildingStoreyAndFileFromFileId(fileId);
    }, [fileId, projectData]) ?? { building: undefined, storey: undefined, file: undefined };

    const storeyId = storey?.id;

    const [isSaving, setIsSaving] = useState(false);

    const initialData = getPlanFileData(storeyId, fileId)?.clone() ?? new PlanFileData();

    const [data, dispatch] = useImmerReducer<PlanFileData, ProjectFilePlanReducerAction>(
        projectFilePlanReducer,
        initialData,
    );

    const [definedScale, setDefinedScale] = useState<number | undefined>(undefined);

    // Maj du contenu si on arrive sur la page directement depuis l'URL.
    useEffect(() => {
        if (data.content !== undefined && !data.content.default) return;
        const updatedData = getPlanFileData(storeyId, fileId);
        if (updatedData) {
            dispatch({ type: 'hydrate', payload: updatedData });
        }
    }, [storeyId, fileId, getPlanFileData, dispatch, data.content]);

    const urlFilePlan = useMemo(() => ENDPOINTS.VIEWER_PDF_PREVIEW(project.id, `${project.projectOrganisation.id}/${project.id}/${file?.relativePath}`), [project.projectOrganisation.id, project.id, file?.relativePath]);

    const saveCurrentFileData = useCallback(async (): Promise<boolean> => {
        if (storeyId == null)
            return false;

        setIsSaving(true);

        try {
            const baamerFileRevisionId = getBaamerFileRevisionIdFromStoreyId(storeyId);
            if (baamerFileRevisionId == null || data.content == null) return false;

            const { position, direction } = data.origin;

            const content =
                data.content instanceof KnownScaleContent
                    ? (() => {
                        // eslint-disable-next-line @typescript-eslint/no-unused-vars
                        const { default: _, ...rest } = data.content;
                        return rest as KnownScaleContent;
                    })()
                    : {
                        segment: data.content.segment,
                        scale: definedScale,
                    } as UserDefinedContent;

            const dto: UpdateStoreyFileDataDTO = { 
                mode: data.content instanceof KnownScaleContent ? ScaleModeType.KNOWN : ScaleModeType.USER,
                origin: { position: { x: position.x, y: position.y, z: position.z }, direction: { x: direction.x, y: direction.y } },
                content: content
            };

            const result = await putItem<unknown>(PROJECT_REFERENCE_ENDPOINTS.UPDATE_STOREY_FILE_DATA(storeyId, baamerFileRevisionId), dto);

            if (result) {
                setPlanFileData(storeyId, fileId, data);
                dispatch({
                    type: 'set-dirty-flag',
                    isDirty: false
                });
                toast.success('Sauvegarde réussie');
            }
        } catch (error) {
            if (error instanceof Error)
                toast.error(error.message, { autoClose: false });
            return false;
        } finally {
            setIsSaving(false);
        }
        
        return true;
    }, [storeyId, fileId, data, definedScale, dispatch, setPlanFileData, getBaamerFileRevisionIdFromStoreyId]);

    const props: ProjectFilePlanContextProps = useMemo(() => ({
        
        building,
        storey,
        
        dataContent: data.content,
        dataOrigin: data.origin,

        urlFilePlan,
        storeyFile: file,

        isDirty: data.isDirty,
        isSaving,

        dispatch,

        setDefinedScale,

        saveCurrentFileData,
    }), [building, storey, data.content, data.origin, data.isDirty, urlFilePlan, file, isSaving, dispatch, saveCurrentFileData]);

    return (
        <ProjectFilePlanContext.Provider value={props}>
            {children}
        </ProjectFilePlanContext.Provider>
    )
}

export default ProjectFilePlanContextProvider;